import { useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface BusinessProfileStatsProps {
  businessImageUrl?: string | null;
  isEditing: boolean;
}

const BusinessProfileStats = ({
  businessImageUrl,
  isEditing
}: BusinessProfileStatsProps) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);

      // Upload image to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const { error: uploadError, data } = await supabase.storage
        .from('professional-photos')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('professional-photos')
        .getPublicUrl(fileName);

      // Update profile with new image URL
      const { error: updateError } = await supabase
        .from('professional_profiles')
        .update({ business_image_url: publicUrl })
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

      if (updateError) throw updateError;

      toast.success('Business image updated successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="w-full md:w-1/3">
      <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-gray-100">
        {businessImageUrl ? (
          <img
            src={businessImageUrl}
            alt="Business"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-purple-50">
            <span className="text-purple-300 text-lg">No image</span>
          </div>
        )}
        {isEditing && (
          <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
              disabled={isUploading}
            />
            <Upload className="w-6 h-6 text-white" />
          </label>
        )}
      </div>
    </div>
  );
};

export default BusinessProfileStats;